import { type ChangeEvent, useCallback, useMemo, useState } from "react";

import { type SupportServiceModel } from "@doitintl/cmp-models";
import { Box, Stack } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { type FormikProps } from "formik";

import { useAuthContext } from "../../Context/AuthContext";
import { useIsFeatureEntitled } from "../../Context/TierProvider";
import CaseIQ from "../../Support/Components/CaseIQ/CaseIQ";
import CategorySuggest from "../../Support/Components/CategorySuggest";
import Editor from "../../Support/Components/Editor/Editor";
import SupportPlatformDropdown from "../../Support/Components/SupportPlatformDropdown";
import { TermsAgreementDialog } from "../../Support/Components/TermsAgreementDialog";
import { useIsDCIEssentialsTier } from "../../Support/hooks/useIsDCIEssentialsTier";
import { type Platform } from "../../Support/types";
import { debouncedContentChange } from "../../Support/utils";
import { AssetSelector } from "./AssetSelector";
import { useAvaCaseIQContext } from "./AvaCaseIQProvider";
import { AvaCreateTicketHeader } from "./AvaCreateTicketHeader";
import { AvaFieldWrapper } from "./AvaFieldWrapper";
import { CallMeCheckbox } from "./CallMeCheckbox";
import { ASSET_NOT_REQUIRED, NO_PROJECT_ASSET } from "./consts";
import { useCaseIQFields } from "./createTicketHook";
import DoerUserSelector from "./DoerUserSelector";
import EmailChipsInput from "./EmailChipsInput";
import { useAssetsList, useCategoryListFromPlatform } from "./hooks";
import { RecentTickets } from "./RecentTickets";
import { SeverityDropdown } from "./SeverityDropdown";
import { type CaseIQFormikValues, type CaseIQTechnicalDetails } from "./types";
import { checkIfCanOpenUrgentTicket } from "./utils";

type Props = {
  bigWidth: boolean;
  formik: FormikProps<CaseIQFormikValues>;
  initialTechnicalDetails: CaseIQTechnicalDetails[];
};

export const AvaCreateTicket = ({ bigWidth, formik, initialTechnicalDetails }: Props) => {
  const [termsAgreementDialogOpen, setTermsAgreementDialogOpen] = useState(false);
  const [contentForCaseIQ, setContentForCaseIQ] = useState<string>("");

  const { billingProfile, setBillingProfile } = useAvaCaseIQContext();
  const { isDoitEmployee } = useAuthContext();

  const { platformList, categoryList, setAccountManagerEmails } = useAvaCaseIQContext();

  const categories = useCategoryListFromPlatform(categoryList, formik.values.platform);

  const isDCIEssentials = useIsDCIEssentialsTier();
  const isEntitledAvaSupport = useIsFeatureEntitled("platform:ava:supportTicket");

  const { assets } = useAssetsList(formik.values.platform);

  const allAssets = useMemo(
    () => (formik.values.priority === "low" ? [NO_PROJECT_ASSET, ...assets] : assets),
    [assets, formik.values.priority]
  );

  const isAssetNotRequired = ASSET_NOT_REQUIRED.includes(formik.values.platform) || formik.values.platform === "";

  const canOpenUrgentTicket =
    checkIfCanOpenUrgentTicket(formik.values.platform, formik.values.cloudProduct, isDCIEssentials) || !!billingProfile;

  const resetUrgentSeverityIfNeeded = useCallback(
    (platform: string, cloudProduct: SupportServiceModel | null) => {
      if (
        !checkIfCanOpenUrgentTicket(platform, cloudProduct, isDCIEssentials) &&
        formik.values.priority === "urgent" &&
        !billingProfile
      ) {
        formik.setFieldValue("priority", "");
      }
    },
    [isDCIEssentials, billingProfile, formik]
  );

  const { caseIQTechnicalDetails } = useCaseIQFields(contentForCaseIQ, formik, initialTechnicalDetails);

  const contentWidth = { width: bigWidth ? 980 : "100%" };

  return (
    <Stack
      px={2}
      py={3}
      alignItems="center"
      sx={{
        overflow: "auto",
        flex: 1,
      }}
      justifyContent="flex-start"
    >
      <AvaCreateTicketHeader bigWidth={bigWidth} />
      <Grid
        container
        spacing={2}
        sx={{
          ...contentWidth,
          flexDirection: bigWidth ? "row" : "column",
        }}
      >
        <Grid
          size={{
            xs: 12,
            md: bigWidth ? 7.5 : 12,
          }}
        >
          <Editor
            placeholder="Please provide a detailed description of your issue"
            name="body"
            minHeight={250}
            value={formik.values.content}
            onChange={(newContent: string) => {
              const htmlContent = `<div>${formik.values.content}</div>`;
              if (newContent.includes("<div>") && htmlContent !== newContent) {
                formik.setFieldValue("content", newContent);

                if (isEntitledAvaSupport) {
                  debouncedContentChange(newContent, setContentForCaseIQ);
                }
              }
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: bigWidth ? 4.5 : 12,
          }}
        >
          {isEntitledAvaSupport && (
            <CaseIQ detectedDetails={caseIQTechnicalDetails} setMinHeight={(_: number) => true} avaChat />
          )}
        </Grid>
      </Grid>

      <CallMeCheckbox
        priority={formik.values.priority}
        cloudPlatform={formik.values.platform as Platform}
        onChange={(checked) => {
          formik.setFieldValue("callMe", checked);
        }}
        bigWidth={bigWidth}
      />
      <Stack
        flexWrap="wrap"
        direction="row"
        sx={{
          ...contentWidth,
          rowGap: 2,
          columnGap: 2,
          mt: 2,
        }}
      >
        {isDoitEmployee && (
          <AvaFieldWrapper bigWidth={bigWidth}>
            <DoerUserSelector
              onChange={(newValue) => {
                formik.setFieldValue("requester", newValue.value);
              }}
            />
          </AvaFieldWrapper>
        )}
        <AvaFieldWrapper bigWidth={bigWidth}>
          <SeverityDropdown
            onChange={(urgency: string) => {
              formik.setFieldValue("priority", urgency);
              formik.setFieldTouched("priority", true);
              if (formik.values.asset?.identifier === NO_PROJECT_ASSET.identifier && urgency !== "low") {
                formik.setFieldValue("asset", "");
              }
            }}
            value={formik.values.priority}
            canOpenUrgentTicket={canOpenUrgentTicket}
            onEscalate={() => {
              setTermsAgreementDialogOpen(true);
            }}
          />
        </AvaFieldWrapper>

        <AvaFieldWrapper bigWidth={bigWidth}>
          <SupportPlatformDropdown
            platformsList={platformList}
            handleChangePlatformField={(e: ChangeEvent) => {
              const newPlatform = (e.target as HTMLSelectElement).value;
              formik.setFieldValue("platform", newPlatform);
              formik.setFieldTouched("platform", true);
              formik.setFieldValue("cloudProduct", null);
              formik.setFieldValue("asset", "");
              resetUrgentSeverityIfNeeded(newPlatform, formik.values.cloudProduct);
            }}
            error={false}
            platform={formik.values.platform}
            hideHelper
          />
        </AvaFieldWrapper>
        <AvaFieldWrapper bigWidth={bigWidth}>
          <CategorySuggest
            categories={categories}
            categoryTitle={formik.values.cloudProduct?.name || ""}
            onChange={(newCloudProduct: SupportServiceModel) => {
              formik.setFieldValue("cloudProduct", newCloudProduct);
              formik.setFieldTouched("cloudProduct", true);
              resetUrgentSeverityIfNeeded(formik.values.platform, newCloudProduct);
            }}
            error={false}
          />
        </AvaFieldWrapper>
        {!isAssetNotRequired && (
          <AvaFieldWrapper bigWidth={bigWidth}>
            <AssetSelector
              onChange={(asset) => {
                formik.setFieldValue("asset", asset);
                formik.setFieldTouched("asset", true);
              }}
              selectedItem={formik.values.asset}
              platform={formik.values.platform}
              assets={allAssets}
            />
          </AvaFieldWrapper>
        )}
        <AvaFieldWrapper bigWidth={bigWidth}>
          <EmailChipsInput
            handleChange={setAccountManagerEmails}
            platform={formik.values.platform}
            priority={formik.values.priority}
          />
        </AvaFieldWrapper>
      </Stack>

      {!canOpenUrgentTicket && (
        <Box sx={{ ...contentWidth }}>
          <TermsAgreementDialog
            open={termsAgreementDialogOpen}
            setOpen={setTermsAgreementDialogOpen}
            billingProfile={billingProfile}
            setBillingProfile={setBillingProfile}
          />
        </Box>
      )}

      <Box sx={{ ...contentWidth, borderTop: "1px solid", pt: 3, mt: 3, borderColor: "divider" }}>
        <RecentTickets />
      </Box>
    </Stack>
  );
};
